import React, {useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {listPublishedDocuments, PublishedDocument} from "../util/api";

/**
 * Everything published to the person signed in.
 *
 * THE FIRST SCREEN OF THE PORTAL AND, FOR MOST VISITS, THE ONLY ONE. Somebody arrives from an
 * email because their procurement team asked for an SBOM. They want to know what they have been
 * given, open it, and leave.
 *
 * NO SEARCH, NO FILTERS, NO BROWSING. There is no way to reach a composition nobody chose to give
 * them, and adding a search box would imply there is something to search for.
 *
 * WHAT EACH ROW SAYS IS DELIBERATE. The product, who published it, how many components, and WHEN
 * IT WAS LAST CHECKED. That last one is the honest half: a published SBOM is a statement about
 * what was known on a particular day, and a list that omits the date invites the reader to assume
 * it was today.
 */
const HEADING = "#2B2B2B";
const MUTED = "#717171";
const BORDER = "#E0E0E0";

function when(value?: string | null): string {
    if (!value) return "not yet";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

export default function Documents({onOpen}: {onOpen: (one: PublishedDocument) => void}) {
    const [rows, setRows] = useState<PublishedDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        listPublishedDocuments().then(result => {
            if (result.status === 200) {
                setRows(result.data?.Results ?? []);
                setError(null);
            } else {
                // THE CODE IS SHOWN rather than swallowed: "nothing has been published to you"
                // and "we could not reach the server" send the reader to different people.
                setError(result.data?.Cause || result.data?.Code
                         || "That list could not be loaded.");
            }
            setLoading(false);
        });
    }, []);

    if (loading) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", py: 10}}>
                <CircularProgress size={24}/>
            </Box>
        );
    }

    return (
        <Box sx={{maxWidth: 900, mx: "auto", px: 3, py: 6, boxSizing: "border-box"}}>
            <Typography component="h1" sx={{fontSize: 24, fontWeight: 500, color: HEADING}}>
                Your documents
            </Typography>
            <Typography sx={{fontSize: 14, color: MUTED, mt: 1, lineHeight: 1.6}}>
                The software bills of materials published to you by the companies you buy from.
            </Typography>

            {error && (
                <Typography sx={{fontSize: 14, color: "#B3261E", mt: 4}}>{error}</Typography>
            )}

            {/* NOTHING YET IS NOT AN ERROR, and the sentence says who to ask rather than leaving
                somebody looking at an empty page wondering whether it is broken. */}
            {!error && rows.length === 0 && (
                <Typography sx={{fontSize: 14, color: MUTED, mt: 4, lineHeight: 1.6}}>
                    Nothing has been published to you yet. The company you bought from decides
                    what appears here &mdash; ask your contact there to publish it.
                </Typography>
            )}

            <Box sx={{mt: 4, display: "flex", flexDirection: "column", gap: 1.5}}>
                {rows.map(one => (
                    <Box key={one.USID}
                         onClick={() => onOpen(one)}
                         sx={{
                             border: `1px solid ${BORDER}`, borderRadius: 2, p: 2.5,
                             cursor: "pointer", backgroundColor: "#FFFFFF",
                             "&:hover": {borderColor: "#1498D8"},
                         }}>
                        <Typography sx={{fontSize: 16, fontWeight: 600, color: HEADING}}>
                            {one.Label || "Untitled"}
                        </Typography>
                        <Typography sx={{fontSize: 13, color: MUTED, mt: 0.5}}>
                            {one.From ? `From ${one.From}` : "From your supplier"}
                            {one.ComponentCount != null && ` · ${one.ComponentCount} components`}
                            {one.Format && ` · ${one.Format}`}
                        </Typography>
                        {/* THE SENTENCE THAT KEEPS THIS HONEST. "Clean" only ever means "clean
                            against what was published that day". */}
                        {/* NEVER SCANNED IS NOT A DATE, and the sentence must not be built as
                            though it were: "checked against advisories published up to not yet"
                            is nonsense, and the honest version is also the more alarming one. A
                            composition nobody has scanned is not clean -- nobody has looked. */}
                        <Typography sx={{fontSize: 12,
                                         color: one.LastScannedDatetime ? MUTED : "#B54708",
                                         mt: 1}}>
                            {one.LastScannedDatetime
                                ? `Checked against advisories published up to ${when(one.LastScannedDatetime)}.`
                                : "Not yet checked against any advisory feed."}
                            {one.ExpiresDate && ` Your access runs to ${when(one.ExpiresDate)}.`}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
